import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { categories_list, status_list } from "../../Shared/List";
import SelectField from "../../Shared/SelectField"
import { FaFileExport } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const LocationBalance = () => {
    const [errors, setErrors] = useState({});
    const [data, setData] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [title, setTitle] = useState('');
    const [byOption, setByOption] = useState([]);
    const [statusOption, setStatusOption] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState({
        status: 1,
        by: 1,
    });
    const {status, by} = formData;

    const fetchData = async (payload) => {
        setTitle(`${status_list[formData.status - 1].name} Receivables by ${categories_list[formData.by - 1].name}`);
        setIsLoading(true)
        try {
            const response = await Server.post('/location-balance/', payload)
            const categories = []
            const statuses = []

            categories_list.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            status_list.map(status => (
                statuses.push({
                    id: status.id,
                    key: status.key,
                    name: status.name
                })
            ))
            
            setData(response?.data);
            setByOption(categories);
            setStatusOption(statuses);
            setIsLoading(false);

            const output = response?.data.reduce((data, row) => {
                data.debit += parseFloat(row.debit);
                data.credit += parseFloat(row.credit);
                return data;
            }, {debit: 0.00, credit: 0.00, balance: 0.00});
            setPinnedBottomRowData([
                {
                    debit: parseFloat(output.debit).toFixed(2),
                    credit: parseFloat(output.credit).toFixed(2),
                    balance: (output.debit - output.credit).toFixed(2),
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error.response.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error.response.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error.response.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail)
            }
        }
    }

    useEffect(() => {
        if (location.state?.by || location.state?.status) {
            const matchedCategory = typeof location.state.by === 'string'
                ? categories_list.find(c => c.name.toLowerCase() === location.state.by.toLowerCase())
                : null;
            const byId = matchedCategory?.id || (typeof location.state.by === 'number' ? location.state.by : 1);
            const statusId = typeof location.state.status === 'string'
                ? status_list.find(s => s.name.toLowerCase() === location.state.status.toLowerCase())?.id || 1
                : location.state.status || 1;
            setFormData(prev => ({
                ...prev,
                by: byId,
                status: statusId,
            }))
            const byStr = matchedCategory?.name || categories_list[byId - 1].name;
            const statusStr = typeof location.state.status === 'string'
                ? location.state.status
                : status_list[statusId - 1].name;
            fetchData({ status: statusStr, by: byStr })
        } else {
            //find category name with its index
            const by = categories_list[formData.by - 1].name
            const status = status_list[formData.status - 1].name
            fetchData({status, by})
        }
    }, [])

    const [columnDefs] = useState([
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        
        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Credit",
            field: "credit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Balance",
            field: "balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
    ]);
    
    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
        
    }

    const onDoubleClick = (payload) => {
        navigate(`/location-balance-detail`, { state: {data: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault()
        
        //find category name with its index
        const by = categories_list[formData.by - 1].name
        const status = status_list[formData.status - 1].name
        const payload = {status, by}
        fetchData(payload)
        
    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }
    
    const onExportClick=() => {
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="status"
                        value={parseInt(status)}
                        options={statusOption}
                        onChange={onChange}
                        error={errors.status}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOption}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-6">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) => {
                                if (e.node.rowPinned) return '';
                                if (e.data.customers < 1) return '';
                                
                                onDoubleClick({status: status_list[formData.status - 1].name, public_id: e.data.public_id, name: e.data.name, by: categories_list[formData.by - 1].name.toLowerCase()})
                            }}

                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            suppressMenuHide={true}
                            
                            rowData={data}
                            domLayout={"normal"}
                            rowSelection={"multiple"}
                        
                            pagination={false}
                            onGridReady={onGridReady}
                        
                            resizable={true}
                            animateRows={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}
                        
                        
                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default LocationBalance